"use client";

import { useCallback, useRef, useState } from "react";
import LiveaboardPendingPhotoGrid from "./LiveaboardPendingPhotoGrid";

const DEFAULT_ACCEPT = "image/png,image/jpeg,image/jpg,image/webp";

type LiveaboardFileDropzoneProps = {
  label?: string;
  hint?: string;
  accept?: string;
  multiple?: boolean;
  disabled?: boolean;
  uploading?: boolean;
  /** Pending files (wizard) — shown as names only, no previews */
  pendingFiles?: File[];
  onPendingFilesChange?: (files: File[]) => void;
  /** Immediate pick/drop — parent handles upload */
  onFilesSelected?: (files: File[]) => void;
};

export default function LiveaboardFileDropzone({
  label,
  hint,
  accept = DEFAULT_ACCEPT,
  multiple = false,
  disabled = false,
  uploading = false,
  pendingFiles = [],
  onPendingFilesChange,
  onFilesSelected,
}: LiveaboardFileDropzoneProps) {
  const fileRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);

  const addFiles = useCallback(
    (incoming: File[]) => {
      const images = incoming.filter((f) =>
        accept.split(",").some((a) => {
          const t = a.trim();
          return f.type === t || f.name.toLowerCase().endsWith(t.replace("image/", "."));
        })
      );
      if (images.length === 0) return;

      if (onPendingFilesChange) {
        onPendingFilesChange(
          multiple ? [...pendingFiles, ...images] : images.slice(0, 1)
        );
      }
      if (onFilesSelected) {
        onFilesSelected(multiple ? images : images.slice(0, 1));
      }
    },
    [accept, multiple, onPendingFilesChange, onFilesSelected, pendingFiles]
  );

  const onDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!disabled && e.dataTransfer?.types?.includes("Files")) setIsDragging(true);
  };

  const onDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const next = e.relatedTarget as Node | null;
    if (!next || !e.currentTarget.contains(next)) setIsDragging(false);
  };

  const onDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.dataTransfer) e.dataTransfer.dropEffect = "copy";
  };

  const onDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    if (disabled || uploading) return;
    addFiles(Array.from(e.dataTransfer?.files ?? []));
  };

  return (
    <div className="space-y-2">
      {label && (
        <label className="block text-xs font-medium text-neutral-600">{label}</label>
      )}
      {hint && <p className="text-xs text-neutral-500">{hint}</p>}

      <div
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") fileRef.current?.click();
        }}
        onDragEnter={onDragEnter}
        onDragLeave={onDragLeave}
        onDragOver={onDragOver}
        onDrop={onDrop}
        onClick={() => !disabled && !uploading && fileRef.current?.click()}
        className={`rounded-xl border-2 border-dashed p-6 text-center cursor-pointer transition-colors ${
          disabled || uploading
            ? "opacity-50 cursor-not-allowed border-neutral-200 bg-neutral-50"
            : isDragging
              ? "border-cyan-600 bg-cyan-50"
              : "border-neutral-300 bg-neutral-50/50 hover:border-cyan-500 hover:bg-cyan-50/40"
        }`}
      >
        <input
          ref={fileRef}
          type="file"
          accept={accept}
          multiple={multiple}
          className="hidden"
          disabled={disabled || uploading}
          onChange={(e) => {
            addFiles(Array.from(e.target.files ?? []));
            e.target.value = "";
          }}
        />
        <p className="text-sm font-medium text-neutral-700">
          {uploading
            ? "Uploading…"
            : isDragging
              ? "Drop files here"
              : "Drag & drop or click to choose"}
        </p>
        <p className="text-xs text-neutral-500 mt-1">PNG, JPG or WebP</p>
      </div>

      {pendingFiles.length > 0 && onPendingFilesChange && (
        <LiveaboardPendingPhotoGrid
          files={pendingFiles}
          onChange={onPendingFilesChange}
        />
      )}
    </div>
  );
}
